import { useCallback, useState } from "react";

import { Edit, Trash } from "components/icons/generated";
import ListEntitiesItemDescription from "components/ListEntitiesItem/Description";
import BadgeNext from "ds/components/BadgeNext";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import TextEllipsis from "ds/components/TextEllipsis";
import Typography from "ds/components/Typography";

import ContextVariablesEditForm from "../ContextVariables/EditForm";
import { ContextAttachedVariable } from "../types";
import styles from "./styles.module.css";

type NewContextVariableProps = {
  item: ContextAttachedVariable;
  onSave?: (index: number, item: ContextAttachedVariable) => void;
  onRemove?: (index: number) => void;
  index: number;
};

const NewContextVariable = ({ item, onSave, onRemove, index }: NewContextVariableProps) => {
  const value = item.isSecret ? "•••••" : item.value;

  const [isEditing, setIsEditing] = useState(false);
  const canEdit = !!onSave;

  const handleRemove = useCallback(() => onRemove?.(index), [index, onRemove]);

  const handleSave = useCallback(
    (item: ContextAttachedVariable) => {
      onSave?.(index, item);
      setIsEditing(false);
    },
    [index, onSave]
  );

  const handleEdit = useCallback(() => setIsEditing(true), []);

  if (isEditing) {
    return <ContextVariablesEditForm index={index} onSave={handleSave} />;
  }

  return (
    <Box
      align="center"
      gap="large"
      className={styles.item}
      padding="large"
      fullWidth
      grid
      gridTemplate={
        canEdit
          ? "minmax(0, 1fr) minmax(0, max-content) min-content"
          : "minmax(0, 1fr) minmax(0, max-content)"
      }
      gridAreas={canEdit ? `"info value actions"` : `"info value"`}
    >
      <Box direction="column" gridArea="info">
        <TextEllipsis tooltip={item.variableName} tooltipWidthMode="maxWidthSm">
          {(props) => (
            <Typography {...props} variant="p-t7" tag="span">
              {item.variableName}
            </Typography>
          )}
        </TextEllipsis>
        <ListEntitiesItemDescription description={item.description} />
      </Box>
      <Box gridArea="value">
        <BadgeNext variant="gray" className={styles.badge} text={value} textEllipsis type="weak" />
      </Box>
      {canEdit && (
        <Box gap="medium" gridArea="actions">
          <ButtonIcon icon={Edit} onPress={handleEdit} variant="ghost">
            Edit
          </ButtonIcon>
          <ButtonIcon icon={Trash} onPress={handleRemove} variant="ghostDanger">
            Delete
          </ButtonIcon>
        </Box>
      )}
    </Box>
  );
};

export default NewContextVariable;
